///
/// EarthBrowser
///
/// Created by Matt Giger on 10/11/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Pure helpers shared by the timeline bar and the keyboard shortcuts:
/// window math, time-zone aware formatting, and frame stepping.

import { type TimeZoneMode } from "@/redux/earthBrowserSlice"

export const cHourMs = 3600_000
export const cDayMs = 24 * cHourMs

/**
 * Stepper stops for the history/forecast offsets (hours). Geometric so the
 * user reaches 48h in a few clicks instead of 48 of them.
 */
export const cOffsetSteps = [0, 1, 2, 3, 6, 12, 24, 48]

/** Next stepper stop above (direction 1) or below (-1) the current value, clamped to [min, max] */
export function stepOffsetHours(current: number, direction: 1 | -1, min: number, max: number): number {
	const stops = cOffsetSteps.filter(h => h >= min && h <= max)
	if (direction > 0) {
		const next = stops.find(h => h > current)
		return next ?? Math.min(max, current)
	}
	const below = stops.filter(h => h < current)
	return below.length > 0 ? below[below.length - 1] : Math.max(min, current)
}

/** The scrub window around an anchor "now", in epoch ms */
export function playbackWindow(anchorMs: number, startOffsetHours: number, endOffsetHours: number): { startMs: number; endMs: number } {
	return {
		startMs: anchorMs - startOffsetHours * cHourMs,
		endMs: anchorMs + endOffsetHours * cHourMs,
	}
}

/** Intl time zone id for a display mode (undefined = the browser's zone) */
export function timeZoneId(mode: TimeZoneMode): string | undefined {
	return mode === "utc" ? "UTC" : undefined
}

/** Short zone abbreviation for the given instant ("UTC", "PDT", "GMT+2"…) */
export function timeZoneLabel(ms: number, mode: TimeZoneMode): string {
	if (mode === "utc") return "UTC"
	const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(new Date(ms))
	return parts.find(p => p.type === "timeZoneName")?.value ?? ""
}

/** "Fri 06:00" in the display zone; used for strip edge labels and the hover tooltip */
export function formatTickTime(ms: number, mode: TimeZoneMode): string {
	return new Intl.DateTimeFormat(undefined, {
		weekday: "short",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		timeZone: timeZoneId(mode),
	}).format(new Date(ms))
}

/** Weekday name only ("Sat"), for midnight tick labels */
export function formatWeekday(ms: number, mode: TimeZoneMode): string {
	return new Intl.DateTimeFormat(undefined, { weekday: "short", timeZone: timeZoneId(mode) }).format(new Date(ms))
}

/** "06:00" in the display zone, no weekday: the strip's edge labels */
export function formatClockTime(ms: number, mode: TimeZoneMode): string {
	return new Intl.DateTimeFormat(undefined, {
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		timeZone: timeZoneId(mode),
	}).format(new Date(ms))
}

/** True when the two instants fall on the same calendar day in the display zone */
export function sameDay(aMs: number, bMs: number, mode: TimeZoneMode): boolean {
	return midnightOnOrBefore(aMs, mode) === midnightOnOrBefore(bMs, mode)
}

/**
 * Strip edge label: the time alone while the edge falls on the same
 * calendar day as the reference instant (typically now), else prefixed
 * with the weekday ("Tue 06:00") so a multi-day window reads correctly.
 */
export function formatEdgeTime(ms: number, referenceMs: number, mode: TimeZoneMode): string {
	return sameDay(ms, referenceMs, mode) ? formatClockTime(ms, mode) : formatTickTime(ms, mode)
}

/**
 * Signed distance of the clock from now as a short "−1h 20m" / "+3h" /
 * "−2d 4h" label; "now" within a minute. Minutes are dropped past a day
 * and when they are zero, so the label stays at most two units long.
 */
export function formatRelativeOffset(deltaMs: number): string {
	const abs = Math.abs(deltaMs)
	if (abs < 60_000) return "now"
	const sign = deltaMs < 0 ? "\u2212" : "+"
	const totalMinutes = Math.round(abs / 60_000)
	const days = Math.floor(totalMinutes / 1440)
	const hours = Math.floor((totalMinutes % 1440) / 60)
	const minutes = totalMinutes % 60
	if (days > 0) return `${sign}${days}d${hours > 0 ? ` ${hours}h` : ""}`
	if (hours > 0) return `${sign}${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`
	return `${sign}${minutes}m`
}

export interface ClockDateTimePart {
	value: string
	/** Year and seconds (with their separator) are dropped on narrow screens */
	compactHidden: boolean
	/** Seconds tick every second; they render dimmer so the eye isn't pulled to them */
	dim: boolean
}

/**
 * Compact date + 24h time in the display zone as locale-ordered parts, no
 * zone suffix: "Oct 28 07:27:59". The year only appears when the instant
 * is outside the current year. Each part is flagged if it can be hidden
 * in the compact (phone) rendering: the year and the seconds plus the
 * separator that attaches each to its neighbour.
 */
export function clockDateTimeParts(ms: number, mode: TimeZoneMode, nowMs: number = Date.now()): ClockDateTimePart[] {
	const zone = timeZoneId(mode)
	const yearOf = (t: number) => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: zone }).format(new Date(t))
	const showYear = yearOf(ms) !== yearOf(nowMs)
	const parts = new Intl.DateTimeFormat(undefined, {
		...(showYear ? { year: "numeric" } : {}),
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hourCycle: "h23",
		timeZone: zone,
	}).formatToParts(new Date(ms))

	const out: ClockDateTimePart[] = parts.map(p => ({
		// most locales separate date and time with ", "; a plain space reads
		// better in the monospace HUD field
		value: p.type === "literal" && p.value.includes(",") ? " " : p.value,
		compactHidden: p.type === "year" || p.type === "second",
		dim: p.type === "second",
	}))
	// Hide the separator that joins a hidden part to the rest: the one
	// before it, or the one after when the hidden part leads (e.g. "2026/")
	parts.forEach((p, i) => {
		if (p.type !== "year" && p.type !== "second") return
		const prev = parts[i - 1]
		const next = parts[i + 1]
		if (prev?.type === "literal" && !prev.value.includes(",") && prev.value.trim() !== "") {
			out[i - 1].compactHidden = true
			if (p.type === "second") out[i - 1].dim = true
		} else if (next?.type === "literal" && !next.value.includes(",") && next.value.trim() !== "") {
			out[i + 1].compactHidden = true
		}
	})
	return out
}

/**
 * Local-midnight instants strictly inside (startMs, endMs) for the display
 * zone. Computed by walking calendar days through Intl so DST shifts land
 * on the real midnight rather than a 24h multiple.
 */
export function midnightsBetween(startMs: number, endMs: number, mode: TimeZoneMode): number[] {
	const out: number[] = []
	if (endMs <= startMs) return out
	// Start from the first midnight on/after startMs by stepping from a
	// day earlier and snapping to local midnight through component parts
	let t = midnightOnOrBefore(startMs, mode)
	for (let guard = 0; guard < 400 && t < endMs; ++guard) {
		if (t > startMs) out.push(t)
		// jump ~a day forward then re-snap to midnight (handles 23/25h days)
		t = midnightOnOrBefore(t + cDayMs + cHourMs * 2, mode)
	}
	return out
}

/** The local midnight at or before the given instant in the display zone */
function midnightOnOrBefore(ms: number, mode: TimeZoneMode): number {
	const d = new Date(ms)
	if (mode === "utc") {
		return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	}
	return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * Nearest frame start strictly after (direction 1) or before (-1) the
 * current time. A 1s tolerance keeps repeated presses from sticking on
 * the frame the clock already shows. Returns null at the ends.
 */
export function adjacentFrameTime(frameTimesMs: number[], currentMs: number, direction: 1 | -1): number | null {
	const tolerance = 1000
	if (direction > 0) {
		for (const t of frameTimesMs) {
			if (t > currentMs + tolerance) return t
		}
		return null
	}
	for (let i = frameTimesMs.length - 1; i >= 0; --i) {
		if (frameTimesMs[i] < currentMs - tolerance) return frameTimesMs[i]
	}
	return null
}
